<template>
    <v-form ref="form" v-model="valid" :disabled="isDisabledClientPrice">
        <v-row>
            <v-col :md="6" :cols="12">
                <span class="text-subtitle-1 font-weight-medium">
                    {{ $t(`${salesTerm.type.toLowerCase()}_price`) }}
                </span>
                <v-row v-if="isShowMarkup" class="mt-1">
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <v-number-input
                                v-model="markupPercent"
                                variant="outlined"
                                persistent-placeholder
                                :prefix="'%'"
                                density="compact"
                                :label="`${$t('markup_percent')}`"
                                hide-details
                            />
                        </div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <v-number-input
                                v-model="markup"
                                variant="outlined"
                                persistent-placeholder
                                density="compact"
                                :label="`${$t('markup_value')}`"
                                hide-details
                            />
                        </div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <v-number-input
                                v-model.number="exchangeRate"
                                variant="outlined"
                                :loading="exchangeRatesLoading"
                                persistent-placeholder
                                density="compact"
                                :disabled="isDisableExchangeRateChange || isDisabledClientPrice"
                                :label="`${$t('exchange_rate')}`"
                                hide-details
                                :rules="rulesExchangeRate"
                            />
                        </div>
                    </v-col>
                    <v-col v-if="!isDisableExchangeRateChange && !isDisabledClientPrice" cols="12" md="3">
                        <div
                            v-ripple
                            style="height: 90%"
                            class="d-flex align-center font-weight-medium cursor-pointer px-3 rounded-sm"
                            @click="applyExchangeRate"
                        >
                            <span class="text-wrap full-width text-primary">
                                {{ $t('apply_actual_rate') }}
                            </span>
                        </div>
                    </v-col>
                </v-row>
                <v-row class="mt-1">
                    <v-col v-if="selectCurrency" cols="12" md="3">
                        <v-select
                            v-model="salesTerm.price.currency"
                            :label="`${$t('currency')}`"
                            :items="currencies"
                            item-title="text"
                            :disabled="singleCurrency"
                            item-value="value"
                            variant="outlined"
                            density="compact"
                            hide-details
                            @update:model-value="changeCurrency"
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-number-input
                            v-model="priceAmountPerRoom"
                            :prefix="salesTerm.price.currency"
                            variant="outlined"
                            class="font-weight-bold"
                            density="compact"
                            :label="`${quantity > 1 ? $t('invoices.amount_per_room') : $t('invoices.amount')}*`"
                            hide-details
                            :rules="[requiredRule('invoices.amount')]"
                        />
                    </v-col>
                    <v-col v-if="quantity > 1" cols="12" md="3" align-self="center" align="center">
                        <div class="text-body-1">{{ `${quantity} ${$t('room', quantity)}` }}</div>
                    </v-col>
                    <v-col v-if="quantity > 1" cols="12" md="3">
                        <v-number-input
                            v-model="priceAmount"
                            :prefix="salesTerm.price.currency"
                            variant="outlined"
                            class="font-weight-bold"
                            density="compact"
                            :label="`${$t('invoices.amount')}*`"
                            hide-details
                            :rules="[requiredRule('invoices.amount_per_room')]"
                        />
                    </v-col>
                    <v-col v-if="isShowCommission" cols="12" md="3">
                        <v-number-input
                            v-model="commissionPercent"
                            :prefix="'%'"
                            :label="`${$t('commission_percent')}`"
                            variant="outlined"
                            density="compact"
                            :disabled="isCommissionsAllDisabled"
                            persistent-placeholder
                            hide-details
                        />
                    </v-col>
                    <v-col v-if="isShowCommission" cols="12" md="3">
                        <v-number-input
                            v-model="commission"
                            :prefix="salesTerm.price.currency"
                            :label="`${$t('commission')}`"
                            variant="outlined"
                            density="compact"
                            :disabled="isCommissionsAllDisabled"
                            hide-details
                            @update:model-value="validateForm"
                        />
                    </v-col>
                    <template v-if="isShowCommissionVat">
                        <v-col cols="12" md="3" />
                        <v-col cols="12" md="3">
                            <v-number-input
                                v-model="commissionVatPercent"
                                :prefix="'%'"
                                :label="`${$t('commission_VAT_percent')}`"
                                :disabled="isCommissionVatDisabled || isDisabledClientPrice || isCommissionsAllDisabled"
                                variant="outlined"
                                density="compact"
                                @update:model-value="validateForm"
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-number-input
                                v-model="commissionVat"
                                :prefix="salesTerm.price.currency"
                                :label="`${$t('commission_VAT')}`"
                                variant="outlined"
                                :disabled="isCommissionVatDisabled || isDisabledClientPrice || isCommissionsAllDisabled"
                                density="compact"
                                :rules="rulesVAT"
                                @update:model-value="validateForm"
                            />
                        </v-col>
                    </template>
                </v-row>
                <v-row
                    v-if="salesTerm.type === 'SUPPLIER' && !$conf.public.account.licenseeForCorporateCompany"
                    no-gutters
                >
                    <v-col cols="12">
                        <v-checkbox
                            v-model="autoCalculateClientPrice"
                            :disabled="isStaticPackage"
                            :label="$t('auto_calculate_client_price')"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <v-col v-if="!disableCancelPenalty" :md="6" :cols="12">
                <span class="text-subtitle-1 font-weight-medium"> {{ $t('cancellation_penalties') }} </span>
                <v-radio-group v-model="cancelPenaltyEnabled" inline class="pt-2">
                    <v-radio :label="$t('no_fees')" :value="false" />
                    <v-radio :label="$t('calculate_cancellation_fees')" :value="true" />
                </v-radio-group>
                <v-row
                    v-for="(penalty, i) in salesTerm.cancelPenalty"
                    :key="`${i}-${penalty.startDateTime}`"
                    align="center"
                >
                    <v-col cols="12" md="4">
                        <v-number-input
                            v-model="penalty.amount"
                            :prefix="salesTerm.price.currency"
                            :label="`${$t('invoices.amount')}`"
                            variant="outlined"
                            class="mt-2"
                            density="compact"
                            hide-details
                            :rules="[requiredRule('invoices.amount')]"
                            @update:model-value="validateForm"
                        />
                    </v-col>
                    <v-col cols="12" md="5">
                        <daterange-m
                            class="mt-2"
                            :label="$t('modify.subtitle_dates')"
                            outlined
                            dense
                            disable-max-date
                            :_start="penalty.startDateTime"
                            :_end="penalty.endDateTime"
                            :rules="[requiredRule('modify.subtitle_dates')]"
                            @change-start="penalty.startDateTime = $event"
                            @change-end="penalty.endDateTime = $event"
                        />
                    </v-col>
                    <v-col cols="12" :md="1" class="d-flex justify-md-end justify-center align-center mt-md-2">
                        <v-btn icon="mdi-close-box-outline" variant="text" @click="removeCancelPenalty(i)" />
                    </v-col>
                </v-row>
                <v-row v-if="cancelPenaltyEnabled" no-gutters class="mt-2">
                    <v-col :cols="12" :md="10" class="d-flex justify-center justify-md-end">
                        <v-btn
                            :color="'primary'"
                            class="mx-auto"
                            icon="mdi-plus-circle-outline"
                            variant="text"
                            @click="addCancelPenalty"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import DaterangeM from '~src/components/search/forms/daterangeM.src'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import {appInstance} from '@/utils/app-accessor'

    @Component({
        components: {DaterangeM},
    })
    export class ModifyManualSalesTerm extends mixins(FormValidationRulesMixin) {
        @VModel() valid
        @Prop({default: false, type: Boolean}) selectCurrency
        @Prop({default: false, type: Boolean}) disableCancelPenalty
        @Prop({default: false, type: Boolean}) singleCurrency
        @Prop({default: false, type: Boolean}) allowNegativeValues
        @Prop({required: true}) clientType
        @Prop({required: true}) supplierPrice
        @Prop({required: true}) clientPrice
        @Prop({required: true}) salesSettings
        @Prop() pricingRules
        @Prop() setContractCurrency
        @Prop({default: false, type: Boolean}) isStaticPackage
        @Prop({default: false, type: Boolean}) isOfflineService

        @Prop({default: () => []}) _salesTerm
        @Prop({default: null}) _bookingXR
        @Prop({default: null}) _serviceCreationXR

        @Prop() serviceStatus

        @Prop({default: 1}) quantity

        cancelPenaltyEnabled = false

        commissionPercentValue = 0
        commissionVatPercentValue = 0
        markupPercentValue = 0
        markupValue = 0
        exchangeRateValue = 0

        exchangeRatesFromAPI = null
        exchangeRatesLoading = false

        autoCalculateClientPrice = true

        isCommissionsAllDisabled = false

        @Emit()
        changeSalesTerm(val) {
            return val
        }

        @Emit()
        changeCurrency(val) {
            return {currency: val, type: this.salesTerm.type}
        }

        @Watch('cancelPenaltyEnabled')
        onCancelPenaltyEnabledChange() {
            if (this.cancelPenaltyEnabled) {
                if (!this.salesTerm.cancelPenalty.length) {
                    this.addCancelPenalty()
                }
            } else {
                this.salesTerm.cancelPenalty = []
            }
        }

        @Watch('quantity')
        quantityChange(val, old) {
            this.priceAmount = (this.priceAmount / old) * val
        }

        async mounted() {
            this.autoCalculateClientPrice = !this.isStaticPackage

            if (this.salesTerm.type === 'CLIENT') {
                if (this.setContractCurrency) {
                    this.salesTerm.price.currency = this.salesSettings?.currency

                    if (!this._bookingXR && !this._serviceCreationXR) {
                        await this.loadExchangeRates()
                        const supplierCurrency = this.supplierPrice?.currency
                        const clientCurrency = this.salesTerm.price.currency

                        this.exchangeRateValue = +(
                            this.exchangeRatesFromAPI[supplierCurrency] / this.exchangeRatesFromAPI[clientCurrency]
                        ).toFixed(4)
                    }
                }

                if (this.isEqualCurrencies) {
                    this.exchangeRateValue = 1
                }

                if (this._bookingXR || this._serviceCreationXR) {
                    if (this.serviceStatus === 'CONFIRMED') {
                        this.exchangeRateValue = this._bookingXR || this._serviceCreationXR
                    } else {
                        this.exchangeRateValue = this._serviceCreationXR || this._bookingXR
                    }
                }
            }

            await this.initCalculatedValues()

            await this.$nextTick
            this.initFinished()
        }

        @Emit()
        initFinished() {}

        async applyExchangeRate() {
            await this.loadExchangeRates()

            const supplierCurrency = this.supplierPrice?.currency
            const clientCurrency = this.salesTerm.price.currency
            this.exchangeRate = (
                this.exchangeRatesFromAPI[supplierCurrency] / this.exchangeRatesFromAPI[clientCurrency]
            ).toFixed(4)
        }

        async loadExchangeRates() {
            try {
                this.exchangeRatesLoading = true

                this.exchangeRatesFromAPI = await appInstance.$api.exchangeRates.get({
                    precision: 10,
                    currencyCode: this.$conf.public.currency.available[0],
                })
            } catch (e) {
                /* empty */
            } finally {
                this.exchangeRatesLoading = false
            }
        }

        addCancelPenalty() {
            this.salesTerm.cancelPenalty.push({
                amount: 0,
                startDateTime: null,
                endDateTime: null,
            })
        }

        async initCalculatedValues() {
            const commissionValueSalesTerms = this.salesTerm.price.commission.amount
            const commissionPercentValueSalesTerms = this.salesTerm.price.commission.percent
            const commissionVatValueSalesTerms = this.salesTerm.price.commissionVatAmount
            const commissionVatPercentValueSalesTerms = this.salesTerm.commissionVatRate

            if (this.pricingRules) {
                const pricingRule = this.pricingRules.pricingRule

                if (this.pricingRules.commissionVatPricingRule && this.salesTerm.type === 'CLIENT') {
                    this.commissionVatPercentValue = this.pricingRules.commissionVatPricingRule.vatValue
                }

                if (commissionPercentValueSalesTerms !== undefined && commissionPercentValueSalesTerms !== 0) {
                    this.commissionPercentValue = commissionPercentValueSalesTerms
                } else if (
                    pricingRule.commissionInCurrency === true &&
                    this.isShowCommission &&
                    this.salesTerm.type === 'CLIENT'
                ) {
                    this.salesTerm.price.commission.amount = pricingRule.commissionValue
                } else if (
                    pricingRule.commissionInCurrency === false &&
                    this.isShowCommission &&
                    this.salesTerm.type === 'CLIENT'
                ) {
                    this.commissionPercent = pricingRule.commissionValue

                    if (commissionValueSalesTerms && this.salesTerm.price.amount === 0) {
                        this.salesTerm.price.commission.amount = commissionValueSalesTerms
                    }
                    if (commissionVatValueSalesTerms && this.salesTerm.price.amount === 0) {
                        this.salesTerm.price.commissionVatAmount = commissionVatValueSalesTerms
                    }
                }

                if (pricingRule.markupValueType === 'PERCENTAGE' && this.isShowMarkup) {
                    this.markupPercentValue = pricingRule.markupValue
                } else if (pricingRule.markupValueType === 'ABSOLUTE' && this.isShowMarkup) {
                    this.markupValue = pricingRule.markupValue
                }
            } else {
                if (commissionPercentValueSalesTerms !== undefined) {
                    this.commissionPercentValue = commissionPercentValueSalesTerms
                } else {
                    this.calculateCommissionPercent()
                }
                if (commissionVatPercentValueSalesTerms !== undefined) {
                    this.commissionVatPercentValue = commissionVatPercentValueSalesTerms
                }
                this.calculateMarkupPercent()
                this.calculateMarkup()
            }

            if (
                (this.serviceStatus === 'CANCELED' || this.serviceStatus === 'CANCELLATION PENDING') &&
                this.salesSettings.applyCommissionToCancelledService === false &&
                !this.isOfflineService
            ) {
                this.commission = 0
                this.commissionVat = 0
                this.isCommissionsAllDisabled = true
            }

            if (this.supplierPrice.amount < 0 || this.clientPrice.amount < 0) {
                if (commissionPercentValueSalesTerms !== undefined) {
                    this.commissionPercent = commissionPercentValueSalesTerms
                }
                if (commissionVatPercentValueSalesTerms !== undefined) {
                    this.commissionVatPercent = commissionVatPercentValueSalesTerms
                }
            }
        }

        calculateMarkupPercent() {
            if (this.supplierPrice?.amount) {
                this.markupPercentValue =
                    ((this.salesTerm.price.amount / this.exchangeRateValue - this.supplierPrice?.amount) /
                        this.supplierPrice?.amount) *
                    100
            }
        }

        calculateMarkup() {
            this.markupValue = this.salesTerm.price.amount / this.exchangeRateValue - this.supplierPrice?.amount
        }

        calculatePriceAmount() {
            if (this.markupPercentValue !== null) {
                const amount =
                    this.supplierPrice?.amount * ((100 + this.markupPercentValue) / 100) * this.exchangeRateValue

                this.salesTerm.price.amount =
                    this.salesSettings?.roundUpPrices && this.salesTerm.type === 'CLIENT' ? Math.ceil(amount) : amount
            }
        }

        calculateCommission() {
            if (this.commissionPercentValue !== null) {
                const amount = (this.salesTerm.price.amount * this.commissionPercentValue) / 100

                this.salesTerm.price.commission.amount =
                    this.salesSettings?.roundUpCommission && this.salesTerm.type === 'CLIENT'
                        ? Math.ceil(amount)
                        : amount
            }
        }

        calculateCommissionPercent() {
            if (this.salesTerm.price.amount) {
                this.commissionPercentValue =
                    (this.salesTerm.price.commission.amount / this.salesTerm.price.amount) * 100
            }
        }

        calculateCommissionVat() {
            if (
                this.isShowCommissionVat &&
                typeof this.commissionVatPercentValue === 'number' &&
                typeof this.salesTerm.price.commission.amount === 'number'
            ) {
                const amount = (this.salesTerm.price.commission.amount * this.commissionVatPercentValue) / 100

                this.salesTerm.price = {
                    ...this.salesTerm.price,
                    commissionVatAmount: this.salesSettings?.roundUpCommission ? Math.ceil(amount) : amount,
                }
            }
        }

        calculateCommissionVatPercent() {
            if (this.salesTerm.price.commission.amount) {
                this.commissionVatPercentValue =
                    (this.salesTerm.price.commissionVatAmount / this.salesTerm.price.commission.amount) * 100
            }
        }

        removeCancelPenalty(i) {
            this.salesTerm.cancelPenalty.splice(i, 1)
        }

        async validateForm() {
            await this.$refs.form.validate()
        }

        @Emit()
        recalculateClientPrice() {}

        get isEqualCurrencies() {
            const supplierCurrency = this.supplierPrice?.currency
            const clientCurrency = this.salesTerm.price.currency

            return supplierCurrency === clientCurrency
        }

        get commissionVat() {
            return +this.salesTerm.price.commissionVatAmount?.toFixed(2)
        }

        set commissionVat(val) {
            this.salesTerm.price.commissionVatAmount = +val
            this.calculateCommissionVatPercent()
        }

        get commission() {
            return +this.salesTerm.price.commission.amount?.toFixed(2)
        }

        set commission(val) {
            this.salesTerm.price.commission.amount = +val
            this.calculateCommissionPercent()
            this.calculateCommissionVat()
        }

        get priceAmount() {
            return +this.salesTerm.price.amount?.toFixed(2)
        }

        set priceAmount(val) {
            this.salesTerm.price.amount = +val
            this.calculateMarkupPercent()
            this.calculateMarkup()

            //check percent
            this.calculateCommission()

            this.calculateCommissionVat()
            if (this.salesTerm.type === 'SUPPLIER' && this.autoCalculateClientPrice) {
                this.recalculateClientPrice()
            }
        }

        get priceAmountPerRoom() {
            return +this.salesTerm.price.amount?.toFixed(2) / this.quantity
        }

        set priceAmountPerRoom(val) {
            this.priceAmount = val * this.quantity
        }

        get commissionVatPercent() {
            return +this.commissionVatPercentValue?.toFixed(2)
        }

        set commissionVatPercent(val) {
            this.commissionVatPercentValue = +val
            this.calculateCommissionVat()
        }

        get exchangeRate() {
            return this.exchangeRateValue !== null ? +this.exchangeRateValue : this.exchangeRateValue
        }

        set exchangeRate(val) {
            this.exchangeRateValue = +val
            this.calculatePriceAmount()
            this.calculateCommission()
            this.calculateCommissionVat()
        }

        get markupPercent() {
            return +this.markupPercentValue?.toFixed(2)
        }

        set markupPercent(val) {
            this.markupPercentValue = +val
            this.calculatePriceAmount()
            this.calculateMarkup()
            this.calculateCommission()
            this.calculateCommissionVat()
        }

        get markup() {
            return +this.markupValue?.toFixed(2)
        }

        set markup(val) {
            this.markupValue = +val
            const priceAmount = this.supplierPrice?.amount * this.exchangeRateValue + this.markupValue
            this.salesTerm.price.amount =
                this.salesSettings?.roundUpPrices && this.salesTerm.type === 'CLIENT'
                    ? Math.ceil(priceAmount)
                    : priceAmount
            this.calculateMarkupPercent()
            this.calculateCommission()
            this.calculateCommissionVat()
        }

        get commissionPercent() {
            return +this.commissionPercentValue?.toFixed(2)
        }

        set commissionPercent(val) {
            this.commissionPercentValue = +val
            this.calculateCommission()
            this.calculateCommissionVat()
        }

        get rulesVAT() {
            const valRule = v => {
                if (+v > 0 && this.salesTerm.price.commission.amount < 0 && !this.allowNegativeValues) {
                    return this.$t('commission_VAT_validation.commission_zero')
                } else if (Math.abs(+v) > Math.abs(this.salesTerm.price.commission.amount)) {
                    return this.$t('commission_VAT_validation.more_than_commission')
                }

                return true
            }
            return [valRule]
        }

        get rulesExchangeRate() {
            const valRule = v => {
                if (+v <= 0) {
                    return this.$t('exchange_rate_validation.less_zero')
                }

                return true
            }
            return [valRule]
        }

        get isShowCommission() {
            if (this.salesTerm.type === 'CLIENT') {
                return this.clientType !== 'DIRECT_SALES' && this.clientType !== 'CORPORATE'
            }

            return true
        }

        get isShowMarkup() {
            return this.salesTerm.type === 'CLIENT'
        }

        get currencies() {
            const availableCurrencies = Object.values(this.$conf.public.currency.available).filter(currency =>
                Object.keys(runtimeStore.exchangeRates).includes(currency)
            )

            return this.singleCurrency
                ? availableCurrencies.filter(c => c === this.salesTerm.price.currency)
                : availableCurrencies
        }

        get isShowCommissionVat() {
            return this.isShowCommission && this.salesTerm.price.commissionVatAmount !== undefined
        }

        get salesTerm() {
            return this._salesTerm
        }

        set salesTerm(val) {
            this.changeSalesTerm(val)
        }

        get isCommissionVatDisabled() {
            return false
        }

        get isDisableExchangeRateChange() {
            return false
        }

        get isDisabledClientPrice() {
            return this.salesTerm.type === 'CLIENT' && this.isStaticPackage
        }
    }

    export default ModifyManualSalesTerm
</script>
